import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Gift } from "lucide-react";
import api, { resolveMedia } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function GiftCard() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [cfg, setCfg] = useState({ amounts: [50, 100, 150, 250, 500], image: "", enabled: true });
  const [amount, setAmount] = useState(100);
  const [form, setForm] = useState({ sender_name: "", recipient_name: "", recipient_email: "", message: "", buyer_email: "" });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    api.get("/gift-cards/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  // Pagina di conferma post-pagamento Stripe
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/gift-cards/confirmation/${sessionId}`);
        if (data.paid) {
          setConfirm(data);
          clearInterval(t);
        }
      } catch { /* ignora */ }
      if (tries > 12) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
  }, [sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", { ...form, amount, website: "" });
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile avviare il pagamento. Riprova.");
      setBusy(false);
    }
  };

  if (sessionId) {
    return (
      <div className="container-x py-20 max-w-2xl" data-testid="giftcard-confirm">
        <p className="eyebrow text-gold">Gift Card</p>
        {confirm ? (
          <>
            <h1 className="font-serif text-4xl md:text-5xl mt-4">La Gift Card è attiva.</h1>
            <div className="mt-8 bg-white border border-hair p-6 md:p-8">
              <p className="text-sm text-taupe">Ordine {confirm.order_number} · {eur(confirm.amount)}</p>
              <p className="mt-4 text-sm">Codice Gift Card:</p>
              <p className="font-mono text-xl tracking-wider mt-1" data-testid="giftcard-code">{confirm.code || "in generazione…"}</p>
              <p className="text-xs text-taupe mt-4 leading-relaxed">La Gift Card digitale è stata inviata a {confirm.recipient_email}. Il codice è utilizzabile per i servizi dello Studio e per lo Shop, anche in più volte, fino a esaurimento del credito. Validità 1 anno.</p>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif text-4xl md:text-5xl mt-4">Conferma del pagamento in corso…</h1>
            <p className="text-taupe mt-4 text-sm">Stiamo verificando il pagamento con la banca. La Gift Card viene generata solo a pagamento confermato: l'aggiornamento è automatico, pochi secondi.</p>
          </>
        )}
      </div>
    );
  }

  // Vendita disattivata dalla dashboard: niente form né pulsanti di acquisto, anche ad accesso diretto.
  if (cfg.enabled === false) {
    return (
      <div className="container-x py-24 max-w-2xl text-center" data-testid="giftcard-disabled">
        <p className="eyebrow text-gold">Gift Card</p>
        <h1 className="font-serif text-4xl md:text-5xl mt-4">Le Gift Card non sono al momento in vendita.</h1>
        <p className="text-taupe mt-5 text-sm leading-relaxed">Le Gift Card già acquistate restano pienamente valide e utilizzabili nel checkout dello Shop e dei servizi con il proprio codice.</p>
        <Link to="/" className="btn-ink mt-8 inline-flex">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-giftcard">
      <section className="container-x py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow text-gold">Gift Card</p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-4">Un credito per la bellezza, da regalare.</h1>
            <p className="text-taupe mt-6 leading-relaxed max-w-lg">La Gift Card Giovanna Matino Interior è un credito unico, utilizzabile sia per i servizi dello Studio acquistabili online sia per i pezzi dello Shop. Il destinatario può usarla anche in più volte: se il credito non copre l'intero importo, paga solo la differenza.</p>
            {cfg.image && <img src={resolveMedia(cfg.image)} alt="Gift Card Giovanna Matino Interior" className="w-full max-w-md mt-8 border border-hair" data-testid="giftcard-image" />}
            <ul className="mt-8 space-y-3 text-sm text-taupe">
              {["Validità 1 anno dalla data di acquisto", "Credito utilizzabile in più soluzioni", "Consegna digitale immediata via email, con messaggio personale"].map((t) => (
                <li key={t} className="flex gap-3"><Check size={15} className="text-gold mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="bg-white border border-hair p-6 md:p-8 space-y-5" data-testid="giftcard-form">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe block mb-2">Scegli l'importo</Label>
              <div className="flex flex-wrap gap-2">
                {(cfg.amounts || []).map((a) => (
                  <button type="button" key={a} onClick={() => setAmount(a)}
                    className={`min-h-[48px] px-5 border text-sm transition-colors ${amount === a ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}
                    data-testid={`giftcard-amount-${a}`}>
                    {eur(a)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome mittente</Label>
                <Input required value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} className="rounded-none mt-1 text-base" data-testid="giftcard-sender" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome destinatario</Label>
                <Input required value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="rounded-none mt-1 text-base" data-testid="giftcard-recipient" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Email destinatario</Label>
              <Input required type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="rounded-none mt-1 text-base" data-testid="giftcard-email" />
              <p className="text-[11px] text-taupe mt-1">La Gift Card digitale viene inviata a questo indirizzo dopo il pagamento.</p>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Tua email (per la ricevuta)</Label>
              <Input type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="rounded-none mt-1 text-base" data-testid="giftcard-buyer-email" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Messaggio personale <span className="normal-case tracking-normal">(facoltativo)</span></Label>
              <Textarea rows={3} maxLength={500} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none mt-1 text-base" data-testid="giftcard-message" />
            </div>
            <button type="submit" disabled={busy} className="btn-ink w-full min-h-[52px] disabled:opacity-40" data-testid="giftcard-submit">
              {busy ? "Preparazione pagamento…" : <>Acquista Gift Card {eur(amount)}</>}
            </button>
            <p className="text-[11px] text-taupe text-center">Pagamento sicuro tramite Stripe. Il codice viene generato solo a pagamento confermato.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
